"use client";

import React, { useEffect, useRef, useState } from "react";
import YouTube, { type YouTubeEvent, type YouTubeProps } from "react-youtube";
import logger from "~/lib/logger";
import { cn } from "~/lib/utils";

/**
 * @param {string} sourceId - The ID of the YouTube video.
 * @param {number | null} seekTime - The time to seek to in milliseconds -- this is used to seek a video already opened or playing.
 * @param {number | null} duration - The duration of the video in milliseconds.
 * @param {boolean} autoPause - Whether to automatically pause the video.
 * @param {number | null} start - The initial start time of the video in seconds - this is used to start the video on firts load; e.g. after dragging.
 * @param {boolean} autoPlay - Whether to automatically play the video once first loaded, used usually with `start`.
 * @param {string} className - The class name to apply to the player container.
 * @param {function} onSeekChange - The function to call when the seek time changes, takes seconds.
 */
interface YouTubePlayerProps {
  sourceId: string;
  seekTime: number | null;
  duration: number | null;
  playTiming: number | null;
  autoPause?: boolean;
  start: number | null;
  autoPlay?: boolean;
  className?: string;
  onSeekChange?: (seekTime: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onSetup?: (player: YouTube) => void;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  sourceId,
  seekTime,
  duration,
  playTiming,
  autoPause,
  start,
  autoPlay,
  className,
  onSeekChange,
  onPlay,
  onPause,
  onSetup,
}) => {
  const playerRef = useRef<YouTube>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player) {
        const internalPlayer = player.getInternalPlayer() as {
          getCurrentTime: () => Promise<number>;
        };
        internalPlayer
          ?.getCurrentTime()
          .then((time) => {
            if (time && onSeekChange) onSeekChange(time);
          })
          .catch((e) =>
            logger.error("Error getting current time", { error: e }),
          );
      }
    };
  }, [onSeekChange]);

  // playTiming is used to force this to refire when the video is RE-played, i.e. when they wan to
  // re-play the video from repeatedly from the same point
  useEffect(() => {
    if (seekTime !== null && playerRef.current && isPlayerReady) {
      const player = playerRef.current.getInternalPlayer() as {
        seekTo: (seconds: number) => void;
        playVideo: () => void;
        pauseVideo: () => void;
        getCurrentTime: () => number;
      };
      if (player && typeof player.seekTo === "function") {
        player.seekTo(Number(seekTime) / 1000);
        player.playVideo();

        if (autoPause && duration) {
          const pauseTimeout = setTimeout(() => {
            player.pauseVideo();
          }, duration);

          return () => clearTimeout(pauseTimeout);
        }
      }
    }
  }, [seekTime, duration, autoPause, isPlayerReady, playTiming]);

  const onPlayerReady = (event: YouTubeEvent) => {
    setIsPlayerReady(true);
    onSetup?.(event.target as YouTube);
  };

  const onPlayerError = (event: YouTubeEvent) => {
    logger.error("YouTube player error:", { code: event.data });
  };

  const opts: YouTubeProps["opts"] = {
    width: "100%",
    height: "100%",
    playerVars: { autoplay: autoPlay ?? 0, start },
  };

  return (
    <div className={cn("relative w-full", className)}>
      <div className="aspect-video">
        <YouTube
          ref={playerRef}
          videoId={sourceId}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          opts={opts}
          onReady={onPlayerReady}
          onError={onPlayerError}
          onPlay={onPlay}
          onPause={onPause}
          className="absolute inset-0 h-full w-full"
        />
      </div>
    </div>
  );
};

export default YouTubePlayer;
